<template>
    <div>
        <Teleport to="body">
            <div v-if="showModal" class="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" @click.self="closeModal">
                <div class="bg-white dark:bg-neutral-900 rounded-lg p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto relative z-[10000]">
                    <button
                        @click="closeModal"
                        class="absolute top-5 right-5 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 z-10"
                    >
                        <i class="fa-fw fa-light fa-sharp fa-times text-xl"></i>
                    </button>

                    <div v-if="currentStep === 'quiz'" class="p-4">
                        <div v-if="currentQuestionIndex < questions.length" class="space-y-6">
                            <div class="border-b border-neutral-200 dark:border-neutral-700 pb-4">
                                <!-- Progress Dots -->
                                <div class="mb-4 flex items-center justify-center gap-2">
                                    <div
                                        v-for="(question, index) in questions"
                                        :key="index"
                                        class="w-2 h-2 rounded-full transition-all duration-300"
                                        :class="{
                                            'bg-[var(--primary-400)]': index === currentQuestionIndex,
                                            'bg-neutral-300 dark:bg-neutral-600': index !== currentQuestionIndex
                                        }"
                                    ></div>
                                </div>
                                <h3 class="text-xl font-semibold text-neutral-900 dark:text-white">
                                    {{ currentQuestion.title }}
                                    <span v-if="currentQuestion.required === false" class="text-sm font-normal text-neutral-500 dark:text-neutral-400">(Optional)</span>
                                </h3>
                                <p v-if="currentQuestion.text" class="text-neutral-600 dark:text-neutral-400 mt-2">{{ currentQuestion.text }}</p>
                            </div>

                            <!-- Text Question -->
                            <div v-if="currentQuestion.type === 'text'" class="space-y-4">
                                <textarea
                                    v-model="answers[currentQuestionIndex]"
                                    class="w-full p-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                                    rows="4"
                                    placeholder="Type your answer here..."
                                ></textarea>
                            </div>

                            <!-- Multi-choice Question -->
                            <div v-if="currentQuestion.type === 'multi-choice'" class="space-y-3">
                                <!-- Single selection (radio) -->
                                <template v-if="!currentQuestion.multiple">
                                    <label
                                        v-for="(option, index) in currentQuestion.options"
                                        :key="index"
                                        class="flex items-start gap-3 p-4 border border-neutral-300 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                                        :class="{ 'bg-primary-50 dark:bg-primary-900/20 border-primary-500': answers[currentQuestionIndex] === option }"
                                    >
                                        <input
                                            type="radio"
                                            :name="`question-${currentQuestionIndex}`"
                                            :value="option"
                                            v-model="answers[currentQuestionIndex]"
                                            class="mt-1"
                                        />
                                        <span class="flex-1 text-neutral-900 dark:text-white">{{ option }}</span>
                                    </label>
                                </template>
                                <!-- Multiple selection (checkbox) -->
                                <template v-else>
                                    <label
                                        v-for="(option, index) in currentQuestion.options"
                                        :key="index"
                                        class="flex items-start gap-3 p-4 border border-neutral-300 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                                        :class="{ 'bg-primary-50 dark:bg-primary-900/20 border-primary-500': isOptionSelected(option) }"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="option"
                                            @change="toggleOption(option)"
                                            :checked="isOptionSelected(option)"
                                            class="mt-1"
                                        />
                                        <span class="flex-1 text-neutral-900 dark:text-white">{{ option }}</span>
                                    </label>
                                </template>
                            </div>

                            <!-- Checkout Question -->
                            <div v-if="currentQuestion.type === 'checkout'" class="space-y-4">
                                <div class="p-6 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-800">
                                    <div class="flex items-baseline gap-2 mb-4">
                                        <span class="text-3xl font-bold text-neutral-900 dark:text-white">$</span>
                                        <span class="text-3xl font-bold text-neutral-900 dark:text-white">{{ formatPrice(currentQuestion.price || 0) }}</span>
                                    </div>
                                    <p v-if="currentQuestion.text" class="text-neutral-600 dark:text-neutral-400">{{ currentQuestion.text }}</p>
                                </div>
                            </div>

                            <div class="flex justify-between pt-4">
                                <button
                                    v-if="currentQuestionIndex > 0"
                                    @click="previousQuestion"
                                    class="px-6 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-900 dark:text-white"
                                >
                                    Previous
                                </button>
                                <button
                                    v-else
                                    class="px-6 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg opacity-50 cursor-not-allowed text-neutral-900 dark:text-white"
                                    disabled
                                >
                                    Previous
                                </button>
                                <button
                                    @click="nextQuestion"
                                    :disabled="!canProceed"
                                    class="px-6 py-2 bg-[var(--primary-400)] text-white rounded-lg hover:bg-[var(--primary-700)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {{ currentQuestionIndex === questions.length - 1 ? 'Complete' : 'Next' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Complete Step -->
                    <div v-if="currentStep === 'complete'" class="text-center space-y-6">
                        <h2 class="text-3xl font-bold text-neutral-900 dark:text-white">{{ completeStep.title }}</h2>
                        <p v-if="completeStep.text" class="text-lg text-neutral-600 dark:text-neutral-400">{{ completeStep.text }}</p>
                        <a
                            v-if="completeStep.cta"
                            :href="completeStep.url || '#'"
                            :target="completeStep.target || '_self'"
                            @click="handleCompleteCta"
                            class="inline-flex items-center justify-center px-8 py-3 text-white rounded-lg text-lg font-semibold transition-colors bg-[var(--primary-400)] hover:bg-[var(--primary-700)] no-underline"
                        >
                            {{ completeStep.cta }}
                        </a>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { trackPurchase } from '../utils/analytics';

const apiUrl = import.meta.env.VITE_API_URL;

export default {
    name: 'Quiz',
    props: {
        quizId: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            default: '',
        },
        description: {
            type: String,
            default: '',
        },
        questions: {
            type: Array,
            default: () => [],
        },
        completeStep: {
            type: Object,
            default: () => ({
                title: '',
                text: '',
                cta: '',
                url: '',
            }),
        },
        blockUuid: {
            type: String,
            default: '',
        },
        pageUuid: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            showModal: false,
            currentStep: 'quiz',
            currentQuestionIndex: 0,
            answers: [],
            quizUuid: null,
        };
    },
    computed: {
        currentQuestion() {
            const question = this.questions[this.currentQuestionIndex] || {};
            // Convert comma-delimited options string to array for rendering
            if (question.type === 'multi-choice' && question.options) {
                return {
                    ...question,
                    options: typeof question.options === 'string'
                        ? question.options.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0)
                        : question.options
                };
            }
            return question;
        },
        canProceed() {
            const answer = this.answers[this.currentQuestionIndex];
            const question = this.currentQuestion;
            
            // If question is not required, always allow proceeding
            if (question.required === false) {
                return true;
            }
            
            // For required questions, check if answer is provided
            if (question.type === 'text') {
                return answer && answer.trim().length > 0;
            } else if (question.type === 'multi-choice') {
                if (question.multiple) {
                    // For multiple selection, check if array exists and has at least one item
                    return Array.isArray(answer) && answer.length > 0;
                } else {
                    // For single selection, check if answer is provided
                    return answer !== undefined && answer !== null && answer !== '';
                }
            } else if (question.type === 'checkout') {
                // Checkout questions always allow proceeding (price is set in question config)
                return true;
            }
            return false;
        },
    },
    methods: {
        saveResponseToSheets() {
            // Only save if block UUID and page UUID are provided
            if (!this.blockUuid || !this.pageUuid) {
                return;
            }

            // Build answers object mapping question UUIDs to answers
            const answersObj = {};
            this.questions.forEach((question, index) => {
                if (question.id) {
                    const answer = this.answers[index];
                    // For multiple choice questions with multiple selection, join array with comma
                    // For single selection or text, use as is (or empty string)
                    if (question.type === 'multi-choice' && question.multiple && Array.isArray(answer)) {
                        answersObj[question.id] = answer.join(', ');
                    } else {
                        answersObj[question.id] = answer || '';
                    }
                }
            });

            // Fire and forget - don't wait for response
            console.log('Saving quiz response with UUID:', this.quizUuid);
            window.axios.post(`${apiUrl}/quiz/response`, {
                quizUuid: this.quizUuid,
                answers: answersObj,
                blockUuid: this.blockUuid,
                pageUuid: this.pageUuid,
            }).catch(error => {
                // Silently fail - don't interrupt the quiz flow
                console.error('Failed to save quiz response to Google Sheets:', error);
            });
        },
        loadQuizUuid() {
            // Load quiz UUID from localStorage using block UUID as the key
            if (!this.blockUuid) return;
            const storageKey = `quiz_uuid_${this.blockUuid}`;
            try {
                const stored = localStorage.getItem(storageKey);
                if (stored) {
                    this.quizUuid = stored;
                }
            } catch (e) {
                console.error('Failed to load quiz UUID from localStorage:', e);
            }
        },
        saveQuizUuid() {
            // Save quiz UUID to localStorage using block UUID as the key
            if (!this.quizUuid || !this.blockUuid) return;
            const storageKey = `quiz_uuid_${this.blockUuid}`;
            try {
                localStorage.setItem(storageKey, this.quizUuid);
            } catch (e) {
                console.error('Failed to save quiz UUID to localStorage:', e);
            }
        },
        openModal() {
            // Load or generate quiz UUID for this user/session
            if (!this.quizUuid) {
                this.loadQuizUuid();
            }
            if (!this.quizUuid) {
                this.quizUuid = uuidv4();
                this.saveQuizUuid();
            }
            this.showModal = true;
            this.currentStep = 'quiz';
            this.currentQuestionIndex = 0;
            // Initialize answers array - use empty array for multiple choice questions, empty string for others
            this.answers = this.questions.map(q => {
                if (q.type === 'multi-choice' && q.multiple) {
                    return [];
                }
                return '';
            });
            // Prevent body scrolling
            document.body.style.overflow = 'hidden';
        },
        closeModal() {
            this.showModal = false;
            this.currentStep = 'quiz';
            this.currentQuestionIndex = 0;
            this.answers = [];
            // Keep quizUuid so reopening uses the same UUID (prevents duplicates)
            // Restore body scrolling
            document.body.style.overflow = '';
        },
        nextQuestion() {
            if (!this.canProceed) return;

            const currentQuestion = this.currentQuestion;
            
            // Handle checkout question type
            if (currentQuestion.type === 'checkout') {
                const price = parseFloat(currentQuestion.price || 0);
                
                // Fire purchased analytics event
                trackPurchase(price);
                
                // Save price as answer for this question
                if (currentQuestion.id) {
                    this.answers[this.currentQuestionIndex] = price.toString();
                }
            }

            // Save all answers in the background (fire and forget)
            this.saveResponseToSheets();

            if (this.currentQuestionIndex < this.questions.length - 1) {
                this.currentQuestionIndex++;
            } else {
                this.currentStep = 'complete';
            }
        },
        formatPrice(price) {
            return parseFloat(price || 0).toFixed(2);
        },
        previousQuestion() {
            if (this.currentQuestionIndex > 0) {
                this.currentQuestionIndex--;
            }
        },
        handleCompleteCta(event) {
            if (!this.completeStep.url || this.completeStep.url === '#') {
                event.preventDefault();
                this.closeModal();
            }
        },
        toggleOption(option) {
            const currentAnswer = this.answers[this.currentQuestionIndex];
            if (!Array.isArray(currentAnswer)) {
                this.answers[this.currentQuestionIndex] = [];
            }
            const index = this.answers[this.currentQuestionIndex].indexOf(option);
            if (index > -1) {
                // Remove option if already selected
                this.answers[this.currentQuestionIndex].splice(index, 1);
            } else {
                // Add option if not selected
                this.answers[this.currentQuestionIndex].push(option);
            }
        },
        isOptionSelected(option) {
            const currentAnswer = this.answers[this.currentQuestionIndex];
            return Array.isArray(currentAnswer) && currentAnswer.includes(option);
        },
    },
    mounted() {
        // Load quiz UUID from localStorage on mount
        this.loadQuizUuid();
        // Listen for open event from parent
        window.addEventListener(`open-quiz-${this.quizId}`, this.openModal);
    },
    beforeUnmount() {
        window.removeEventListener(`open-quiz-${this.quizId}`, this.openModal);
        // Ensure body scrolling is restored if component is unmounted while modal is open
        document.body.style.overflow = '';
    },
};
</script>

<style scoped>
</style>

